import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useSelector } from "react-redux";

import { db, functions } from "../../config/firebaseConfig";
import { selectActiveUid } from "../Auth/authSlice";

const MAX_MESSAGES = 50;

function formatTime(ts) {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function MessageBubble({ msg }) {
  const isOut = msg.direction === "out";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isOut ? "flex-end" : "flex-start",
        mb: 1,
      }}
    >
      <Box sx={{ maxWidth: "78%" }}>
        {!isOut && (
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            {msg.sender}
          </Typography>
        )}
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: isOut ? "primary.main" : "grey.200",
            color: isOut ? "primary.contrastText" : "text.primary",
            borderRadius: isOut
              ? "18px 18px 4px 18px"
              : "18px 18px 18px 4px",
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {msg.text}
          </Typography>
        </Paper>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: isOut ? "right" : "left", px: 1, mt: 0.25 }}
        >
          {formatTime(msg.ts)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function StationChat() {
  const activeUid = useSelector(selectActiveUid);
  const displayName = useSelector((state) => state.auth.displayName);
  const station = useSelector((state) => state.auth.station);

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  // Real-time listener on chat_messages subcollection
  useEffect(() => {
    if (!activeUid) return;

    const q = query(
      collection(db, "users", activeUid, "chat_messages"),
      orderBy("ts", "desc"),
      limit(MAX_MESSAGES),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .reverse(); // oldest first for display
        setMessages(msgs);
      },
      (err) => {
        console.error("StationChat: onSnapshot error", err);
      },
    );

    return () => unsub();
  }, [activeUid]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || !activeUid || sending) return;

    setSending(true);
    setError("");
    setText("");

    try {
      const sendFn = httpsCallable(functions, "stationChatSend");
      await sendFn({
        uid: activeUid,
        text: trimmed,
        sender: displayName || station || "Station",
      });
    } catch (err) {
      console.error("StationChat: send error", err);
      setError("Envoi impossible. Reessayez.");
      setText(trimmed); // restore text on error
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        m: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 160px)",
        }}
      >
        {/* Messages list */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 1,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {messages.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 4 }}
            >
              Aucun message. Ecrivez pour contacter votre canal Slack.
            </Typography>
          )}
          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}
          <div ref={bottomRef} />
        </Box>

        {error && (
          <Typography color="error" variant="caption" sx={{ px: 1, mb: 0.5 }}>
            {error}
          </Typography>
        )}

        {/* Input bar */}
        <Paper variant="outlined" sx={{ p: 1 }}>
          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ecrire un message..."
            multiline
            maxRows={4}
            fullWidth
            size="small"
            disabled={sending}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSend}
                    disabled={!text.trim() || sending || !activeUid}
                    color="primary"
                    size="small"
                  >
                    {sending ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
}
